/**
 * Created by Ning Jiang on 2021-06-22.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {TimelineLite, TweenLite} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {BigWinView} from "./BigWinView";
import {ElGordosRevenge} from "../../../ElGordosRevenge";
import {ElGordosRevengeGameConfig} from "../../../ElGordosRevengeGameConfig";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {ElGordosRevengeSound} from "../../../sound/ElGordosRevengeSound";

export class BigWinController extends WinPresentationController {

    private _view:BigWinView;
    private _levelTimeline:TimelineLite | null;

    private _belowStakeWinRestriction:boolean;
    private _levelSkipped:boolean;

    constructor() {
        super("BigWinController", true);
        this._view = new BigWinView();
    }

    protected hasWin():boolean {
        return ElGordosRevenge.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1;
    }

    protected async createWinAnimation():Promise<any> {
        this._belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowPlayedBetValue();
        if(this._belowStakeWinRestriction) {
            const tl = new TimelineLite();
            tl.add(() => {}, 0.1);
            await Promise.resolve(tl);
        } else {
            ElGordosRevenge.playScattersWin = true;
            await new Promise<any>(resolve => {
                this.playBigWinLevel(0, resolve);
            });
        }
    }

    private playBigWinLevel(level:number, resolve:(value:any) => void):void {
        const data = ElGordosRevenge.model.parsedGameData;
        if(level > data.currentWinRatioConfig!.bigWinLevel) {
            this._levelTimeline = null;
            this.playEnding(data.currentWinRatioConfig!.bigWinLevel, resolve);
        } else {
            const isEndLevel = level === data.currentWinRatioConfig!.bigWinLevel;
            const currentRatio = ElGordosRevengeGameConfig.getWinRatioConfig(data.mode).find(r => r.bigWinLevel === level)!;
            const from:number = level === 0 ? 0 : data.playedBetValue * currentRatio.ratio;
            const to:number = isEndLevel ? data.singleWin : data.playedBetValue * ElGordosRevengeGameConfig.getWinRatioConfig(data.mode).find(r => r.bigWinLevel === (level + 1))!.ratio;
            const tooSmallEndLevel:boolean = isEndLevel && this.isLevelWinTooSmall(level, from, to);
            const tl = new TimelineLite();
            tl.add(this.getLevelAnimation(level, currentRatio.duration, from, to, isEndLevel));
            tl.add(() => this.playBigWinLevel(level + 1, resolve));
            this._levelTimeline = tl;
            if(tooSmallEndLevel) {
                this._levelTimeline.progress(1);
            } else {
                this._flowStateController.setSkipCallback(() => this.skip!());
                ElGordosRevengeSound.soundPlayer.playTrack({name : ElGordosRevengeSound.BIG_WIN[level]});
                ElGordosRevengeSound.soundPlayer.playTrack({
                    name : ElGordosRevengeSound.BIG_WIN_COUNT_UP,
                    volume : level > 2 ? 1 : 0.7
                });
            }
        }
    }

    private isLevelWinTooSmall(level:number, from:number, to:number):boolean {
        if(level === 0) {
            return false;
        }

        return (to - from) <= (from * 0.1);
    }

    private getLevelAnimation(level:number, duration:number, from:number, to:number, isEndLevel:boolean):TimelineLite {
        ElGordosRevenge.bigWinCountUp.onEnd(() => {
            if(!this._levelSkipped) {
                ElGordosRevengeSound.soundPlayer.playTrack({name : ElGordosRevengeSound.BIG_WIN_COUNT_UP_ENDS});
            }
        });
        const px = ElGordosRevenge.bigWinCountUp.startPosition.x;
        const py = ElGordosRevenge.bigWinCountUp.startPosition.y;
        const tl = new TimelineLite();
        tl.add(() => {
            this._levelSkipped = false;
            ElGordosRevenge.bigWinCountUp.scale.set(1.3);
        });
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py + 8}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px - 8, y : py + 8}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py + 8}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px + 8, y : py - 8}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py + 8}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py + 8}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px - 8, y : py + 8}));
        tl.add(TweenLite.to(ElGordosRevenge.bigWinCountUp, 0.066, {x : px, y : py}));
        tl.add([
            ElGordosRevenge.bigWinCountUp.getCountUpAnimation(from, to, duration),
            TweenLite.to(ElGordosRevenge.bigWinCountUp.scale, duration, {x : 1, y : 1, ease : `power4.out`}),
            this._view.getLevelAnimation(level, duration, isEndLevel)
        ], 0);
        return tl
    }

    protected skip() {
        this._levelSkipped = true;
        ElGordosRevengeSound.BIG_WIN.forEach(name => {
            ElGordosRevengeSound.soundPlayer.stopTrack(name);
        });
        ElGordosRevengeSound.soundPlayer.stopTrack(ElGordosRevengeSound.BIG_WIN_INTRO);
        ElGordosRevengeSound.soundPlayer.stopTrack(ElGordosRevengeSound.BIG_WIN_COUNT_UP);
        if(this._levelTimeline && this._levelTimeline.isActive()) {
            this._levelTimeline.progress(1);
        }
    }

    private playEnding(endLevel:number, resolve:(value:any) => void):void {
        this._flowStateController.setSkipCallback();
        ElGordosRevengeSound.soundPlayer.playTrack({name : ElGordosRevengeSound.BIG_WIN_END});
        if(!ElGordosRevenge.model.parsedGameData.brokeTheBank) {
            ElGordosRevengeSound.fadeMusic(1, 200);
            if(ElGordosRevenge.background.isBoostLooping) {
                ElGordosRevengeSound.soundPlayer.fadeTrack({
                    name : ElGordosRevengeSound.BG_FIRE_LOOP,
                    to : 1,
                    duration : 100
                });
            }
        }
        if(ElGordosRevenge.model.parsedGameData.brokeTheBank) {
            ElGordosRevenge.bigWinCountUp.remove();
            ElGordosRevenge.waysWon.remove();
        } else {
            ElGordosRevenge.bigWinCountUp.stay();
            ElGordosRevenge.waysWon.remove();
        }
        SlotGame.keypadPlugin.setWin(ElGordosRevenge.model.parsedGameData.totalWin, ElGordosRevenge.model.parsedGameData.showTotalWin, BelowStakeWinRestriction.isWinBelowStake());
        this._view.playEnding(endLevel);
        resolve(this);
        // TODO:
        // const tl = new TimelineLite();
        // tl.add(() => {
        //     resolve(this);
        // }, DeadwoodRip.model.parsedGameData.brokeTheBank ? 0 : 0.9);
    }
}